/** Dial codes for inline phone inputs (storefront). */

export type PhoneDialOption = {
  code: string;
  iso: string;
  label: string;
};

export const PHONE_DIAL_OPTIONS: PhoneDialOption[] = [
  { code: "60", iso: "MY", label: "MY +60" },
  { code: "65", iso: "SG", label: "SG +65" },
  { code: "66", iso: "TH", label: "TH +66" },
  { code: "62", iso: "ID", label: "ID +62" },
  { code: "63", iso: "PH", label: "PH +63" },
  { code: "84", iso: "VN", label: "VN +84" },
  { code: "852", iso: "HK", label: "HK +852" },
  { code: "86", iso: "CN", label: "CN +86" },
  { code: "61", iso: "AU", label: "AU +61" },
  { code: "44", iso: "GB", label: "GB +44" },
  { code: "1", iso: "US", label: "US +1" },
];

const ISO_TO_DIAL: Record<string, string> = Object.fromEntries(
  PHONE_DIAL_OPTIONS.map((o) => [o.iso, o.code]),
);

const DIAL_CODES_DESC = [...PHONE_DIAL_OPTIONS.map((o) => o.code)].sort((a, b) => b.length - a.length);

export function dialCodeForCountry(iso: string | undefined): string {
  if (!iso) return "60";
  return ISO_TO_DIAL[iso.toUpperCase()] ?? "60";
}

export function parsePhoneParts(
  value: string,
  defaultDialCode = "60",
): { dialCode: string; localNumber: string } {
  let raw = value.replace(/[\s\-+()]/g, "");
  if (!raw) return { dialCode: defaultDialCode, localNumber: "" };

  if (raw.startsWith("00")) raw = raw.slice(2);

  for (const code of DIAL_CODES_DESC) {
    if (raw.startsWith(code) && raw.length > code.length) {
      return { dialCode: code, localNumber: stripLeadingZero(raw.slice(code.length)) };
    }
  }

  if (raw.startsWith("0")) {
    return { dialCode: defaultDialCode, localNumber: raw.slice(1) };
  }

  return { dialCode: defaultDialCode, localNumber: raw };
}

function stripLeadingZero(s: string): string {
  return s.startsWith("0") ? s.slice(1) : s;
}

/** Combine dial code + local digits into E.164 without plus (e.g. 60123456789). */
export function combinePhoneParts(dialCode: string, localNumber: string): string {
  const digits = localNumber.replace(/[\s\-+()]/g, "");
  if (!digits) return "";
  return `${dialCode}${stripLeadingZero(digits)}`;
}

export function formatPhoneDisplay(value: string): string {
  if (!value.trim()) return "";
  const { dialCode, localNumber } = parsePhoneParts(value);
  const display =
    dialCode === "60" && localNumber ? `0${localNumber}` : localNumber;
  return `+${dialCode} ${display}`;
}
